import { Client } from "urql";
import { useFragment } from "../gql";
import { PlaylistFieldsFragment } from "../gql/graphql";
import { LocalStorageKey } from "../localStoragePersistence/common/LocalStorageKey";
import {
  playlistFieldsFragment,
  playlistsDeltaQuery,
  playlistsFullQuery,
  playlistsIdsQuery,
} from "../playlist/graphql/playlistDocuments";
import { calculateNewWatermark } from "./Watermark";
import { mutationsInFlight } from "../offline/MutationsInFlight";

const maxWaitForMutationsMs = 10_000;

export const playlistsLastUpdatedAtKey = new LocalStorageKey(
  "App",
  "playlistsLastUpdatedAt",
);

/**
 * Syncs the playlists like the songs (ADR-11):
 * 1. wait until no mutation is in flight, otherwise the ids hide a playlist whose creation is still on the way
 * 2. all ids: deleted or no longer accessible playlists drop out of the list
 * 3. the delta since the watermark
 * 4. the full list from the cache
 *
 * Mutations still queued offline need no wait: the ids query fails as well then.
 */
export function syncPlaylists(
  graphQlClient: Client,
  whenNoMutationInFlight: () => Promise<void> = () =>
    mutationsInFlight.whenIdle(maxWaitForMutationsMs),
): Promise<readonly PlaylistFieldsFragment[]> {
  const lastUpdatedAt =
    window.localStorage.getItem(playlistsLastUpdatedAtKey.localStorageKey) ||
    undefined;

  return whenNoMutationInFlight()
    .then(() =>
      graphQlClient
        .query(playlistsIdsQuery, {}, { requestPolicy: "network-only" })
        .toPromise(),
    )
    .then((result) =>
      result.error ? Promise.reject(result.error) : Promise.resolve(),
    )
    .then(() =>
      graphQlClient
        .query(
          playlistsDeltaQuery,
          { updatedSince: lastUpdatedAt },
          { requestPolicy: "network-only" },
        )
        .toPromise(),
    )
    .then((result) => {
      if (result.error || !result.data) {
        return Promise.reject(result.error);
      }
      const delta = useFragment(playlistFieldsFragment, result.data.playlists);
      const newWatermark = calculateNewWatermark(lastUpdatedAt, delta);
      if (newWatermark !== undefined) {
        window.localStorage.setItem(
          playlistsLastUpdatedAtKey.localStorageKey,
          newWatermark,
        );
      }
      return loadPlaylistsFromCache(graphQlClient);
    });
}

function loadPlaylistsFromCache(
  graphQlClient: Client,
): Promise<readonly PlaylistFieldsFragment[]> {
  const load = (requestPolicy: "cache-only" | "cache-first") =>
    graphQlClient
      .query(playlistsFullQuery, {}, { requestPolicy })
      .toPromise()
      .then((result) =>
        result.data
          ? useFragment(playlistFieldsFragment, result.data.playlists)
          : Promise.reject(result.error ?? "cache miss"),
      );
  // A playlist that came back with the ids but not with the delta (e.g. access regained) is not complete in the cache
  return load("cache-only").catch(() => load("cache-first"));
}
